import { useParams, useNavigate } from 'react-router-dom'
import { useLayoutEffect, useMemo, useState } from 'react'
import SourcingComponent from '../../sourcing/SourcingComponent'
import { calculateTotalSpent } from '../../../services/import/importProductIdsService'
import { adjustOptionsQuantities, adjustBundleQuantities } from '../../../services/sourcing/sourcingHelper'
import { searchProducts } from '../../../services/search/SearchProductsService'
import { useAppReportsStore } from '../../../stores/appReportsStore'
import { Product, OptionsBundle } from '../../../stores/appReportsStore'
import { getScrollY, saveScrollY } from '../../../services/helperService'

export default function ReportDetailPage() {
  const navigate = useNavigate()
  const { reportId, articleId: editId } = useParams<{
    reportId: string
    articleId?: string
  }>()

  const report = useAppReportsStore(s =>
    reportId ? s.getReport(reportId) : undefined
  )
  const productsMap = useAppReportsStore(s =>
    reportId ? s.getProductsIdMap(reportId) : undefined
  )
  const selectedOptionIdMap = useAppReportsStore(s =>
    reportId ? s.getSelectedOptionIdMap(reportId) : undefined
  )
  const optionsBundle = useAppReportsStore(s =>
    reportId ? s.getOptionsBundle(reportId) : undefined
  )
  const updateSelectedOptionIdMap = useAppReportsStore(s => s.updateSelectedOptionIdMap)
  const setProductsIdMap = useAppReportsStore(s => s.setProductsIdMap)
  const setOptionsBundle = useAppReportsStore(s => s.setOptionsBundle)

  // local UI state
  const [searchLoading, setSearchLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  // derived
  const order = useMemo(() => {
    if (!productsMap) return []
    return Object.values(productsMap)
      .sort((a, b) => calculateTotalSpent(b) - calculateTotalSpent(a))
      .map(p => p.articleId)
  }, [productsMap])

  const sortedProducts = order.map(id => productsMap![id])
  const selectedPairs = order
    .map(id => ({ parentId: id, product: selectedOptionIdMap![id]! }))
    .filter(p => p.product)

  const hasOptions = selectedPairs.length === sortedProducts.length
  const hasFetchedOptions = Boolean(optionsBundle && Object.keys(optionsBundle).length)

  // callbacks
  const handleQuantityChange = (articleId: string, value: string) => {
    const qty = Math.max(1, parseInt(value) || 1)
    const prod = productsMap![articleId]
    if (!prod) return

    const updatedProducts: Record<string, Product> = {
      ...productsMap!,
      [articleId]: { ...productsMap![articleId], quantity: qty },
    };

    setProductsIdMap(reportId!, updatedProducts);

    if (!hasOptions) return
    const sourcingRec = Object.fromEntries(Object.entries(optionsBundle!).map(([id, b]) => [id, b.sourcingOptions]))
    const searchRec   = Object.fromEntries(Object.entries(optionsBundle!).map(([id, b]) => [id, b.searchOptions]))
    const adjS = adjustOptionsQuantities(updatedProducts, sourcingRec)
    const adjQ = adjustOptionsQuantities(updatedProducts, searchRec)
    const rebuilt: Record<string, OptionsBundle> = {}
    for (const id of Object.keys(optionsBundle!)) {
      rebuilt[id] = { sourcingOptions: adjS[id]||[], searchOptions: adjQ[id]||[] }
    }
    setOptionsBundle(reportId!, rebuilt)
    const newSel = { ...selectedOptionIdMap! }
    for (const id of Object.keys(rebuilt)) {
      const prev = newSel[id]; if (!prev) continue
      const found = rebuilt[id].sourcingOptions.find(o => o.articleId===prev.articleId)
      if (found) newSel[id]=found
    }
    updateSelectedOptionIdMap(reportId!, newSel)
  }

  const handleSelectOption = (opt: Product) => {
    if (!editId) return
    updateSelectedOptionIdMap(reportId!, { ...selectedOptionIdMap!, [editId]: opt })
    navigate(`/reports-legacy/${reportId}`)
  }

  const handleSearch = async (term: string) => {
    setSearchLoading(true)
    try {
      const parentCat = selectedOptionIdMap![editId!]?.category
      const map = await searchProducts([term], report?.restaurantId!, parentCat)
      return map[term] || []
    } finally {
      setSearchLoading(false)
    }
  }

  const handleUpdateOptions = (newOpts: Product[]) => {
    if (!editId) return
    const existing = optionsBundle![editId] ?? { sourcingOptions: [], searchOptions: [] }
    const updated: OptionsBundle = { ...existing, searchOptions: newOpts }
    setOptionsBundle(reportId!, adjustBundleQuantities(productsMap!, { ...optionsBundle!, [editId]: updated }))
  }

  const handleEdit = (id: string) => {
    const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
    const y = scroller?.scrollTop ?? 0;
    saveScrollY(y);
    navigate(`/reports-legacy/${reportId}/edit/${id}`)
  }
  const handleCancelEdit = () => navigate(`/reports-legacy/${reportId}`)

  const navigateToSummary = () => {
    const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
    const y = scroller?.scrollTop ?? 0;
    saveScrollY(y);
    navigate(`/reports-legacy/${reportId}/summary`)
  }

  const handleRemoveProduct = (id: string) => {
    const newProducts = { ...productsMap }
    delete newProducts[id]
    setProductsIdMap(reportId!, newProducts)
  
    const newBundle = { ...optionsBundle }
    delete newBundle[id]
    setOptionsBundle(reportId!, newBundle)
  
    const newSelected = { ...selectedOptionIdMap }
    delete newSelected[id]
    updateSelectedOptionIdMap(reportId!, newSelected)
  }

  const handleGoBack = () => {
    if (editId) {
      handleCancelEdit()
    } else {
      navigate('/reports-legacy')
    }
  }

  useLayoutEffect(() => {
    if (editId) return;
    const y = getScrollY();
    const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
    if (!scroller) return;
  
    const maxNeeded = y + scroller.clientHeight;
  
    const tryRestore = () => {
      if (scroller.scrollHeight >= maxNeeded) {
        scroller.scrollTop = y;
      } else {
        requestAnimationFrame(tryRestore);
      }
    };
    requestAnimationFrame(tryRestore);
  }, [editId, sortedProducts.length]);

  if (!report) return <div className="p-6">Report not found.</div>

  return (
    <div className="min-h-full p-2 min-[500px]:p-6 pt-4">
      <SourcingComponent
        source="report"
        sortedProducts={sortedProducts}
        selectedPairs={selectedPairs}
        productsMap={productsMap!}
        optionsBundle={optionsBundle!}
        selectedOptionIdMap={selectedOptionIdMap!}
        fileMetadata={{
          fileName: report.name,
          startDate: report.startDate,
          endDate: report.endDate,
        }}
        editId={editId}
        hasFetchedOptions={hasFetchedOptions}
        hasOptions={hasOptions}
        loading={false}
        isModalOpen={modalOpen}
        onGetOptions={() => {}}
        pageTitle={report.name}
        handleGoBack={handleGoBack}
        onSaveClick={() => setModalOpen(true)}
        onSummaryClick={navigateToSummary}
        onQuantityChange={handleQuantityChange}
        onEdit={handleEdit}
        onSelectOption={handleSelectOption}
        onCancelEdit={handleCancelEdit}
        onCloseModal={() => setModalOpen(false)}
        searchOptions={optionsBundle![editId!]?.searchOptions || []}
        searchLoading={searchLoading}
        onSearch={handleSearch}
        onUpdateOptions={handleUpdateOptions}
        onDeleteProduct={handleRemoveProduct}
        saveModalMode="update"
        reportId={reportId}
        restaurantId={report.restaurantId}
      />
    </div>
  )
}

